//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { makeStyles, DialogTitle, DialogContent, DialogActions, Dialog, Tooltip, Badge } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import MessageIcon from '@material-ui/icons/Message';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import Avatar from '@material-ui/core/Avatar';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { getEngagementRemainingText, getNameByUser } from '../../common/textHelper';
import {
    EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED,
    EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_APPLIED,
    CONFIRM_DIALOG_TITLE_DELETE_SAVED_JS,
    CONFIRM_DIALOG_MESSAGE_DELETE_SAVED_JS,
    CONFIRM_DIALOG_TITLE_DELETE_SAVED_APPLICANT,
    CONFIRM_DIALOG_MESSAGE_DELETE_SAVED_APPLICANT,
} from '../../common/constants';
import { deleteSavedJobSeekerFromMyJobs, deleteApplicantsFromMyJobs } from '../../common/services/empDashboardServices';
import { getEngagementRemainingCountByEmployerTiersData } from '../../common/employerHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { clearUnreadNewApplicants } from '../../common/services/empMyJobsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import InterviewDialog from '../../empTalentCenter/subComponents/InterviewDialog';
import SendMessageDialog from '../../empTalentCenter/subComponents/SendMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ProfileDialog from './ProfileDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _includes from 'lodash/includes';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { remainingEngagementVisible } = params;

    return makeStyles(theme => ({
        closeButton: { margin: 10 },
        avatar: { color: 'rgb(252,130,31)', background: 'rgba(252, 130, 31, 0.1)', marginRight: 20, height: 50, width: 50 },
        icons: { fontSize: 24, margin: '0 10px', cursor: 'pointer' },
        iconsDisabled: { fontSize: 24, margin: '0 10px', color: '#BBB' },
        boldFont: { fontWeight: 'bold' },
        emailText: { color: 'rgb(155,158,156)' },
        flexContainer: {
            display: 'flex',
            alignItems: 'center',
            margin: '0 10px',
            padding: '10px 7px',
            cursor: 'pointer',
            '&:hover': { borderRadius: 4, backgroundColor: 'rgba(0,0,0,0.06)' },
        },
        buttonLabel: {
            fontWeight: 700,
            fontSize: '14px',
        },
        // dp: { width: 40, height: 40, borderRadius: 50 },
        container: {
            display: 'flex',
            // alignItems: 'center',
            justifyContent: 'space-between',
            padding: 10,
            '&:hover': { backgroundColor: 'rgb(240,242,246)', borderRadius: 4 },
            [theme.breakpoints.up('md')]: { alignItems: 'center' },
            [theme.breakpoints.down('sm')]: { flexDirection: 'column', borderBottom: '1px solid #D5D7DD' },
        },
        engagementRemainingCountContainer: { marginBottom: 10 },
        engagementRemainingCountText: { fontWeight: 'bold' },
        profilePicContainer: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
        profilePicMainContainer: { display: 'flex', alignItems: 'center', minWidth: '215px' },
        nameContainer: { flex: 4 },
        requestInterviewContainer: { flex: 3, minWidth: 200, textAlign: 'center', [theme.breakpoints.down('sm')]: { borderBottom: '1px solid #D5D7DD', width: '100%' } },
        sendMessageContainer: {
            flex: 1,
            textAlign: 'center',
            [theme.breakpoints.down('sm')]: { borderRight: '1px solid #D5D7DD' },
            [theme.breakpoints.up('md')]: { minWidth: 200 },
        },
        viewProfileContainer: { flex: 1, textAlign: 'center' },
        deleteButtonContainer: { flex: 1, [theme.breakpoints.up('md')]: { textAlign: 'right' } },
        dialogTitle: { paddingBottom: 0 },
        title: { fontWeight: 'bold' },
        jobTitleText: { fontStyle: 'italic', textDecoration: 'underline' },
        dialogButtonContainerSm: { display: 'flex', alignItems: 'center', flexDirection: 'column', border: '1px solid #D5D7DD', marginTop: '10px', borderRadius: '5px' },
        twoButtonsContainerSm: { display: 'flex', alignItems: 'center', borderBottom: '1px solid #D5D7DD', width: '100%' },
        listMainContainer: {
            paddingTop: remainingEngagementVisible ? 0 : 8,
            [theme.breakpoints.down('sm')]: {
                maxHeight: '400px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                '&::-webkit-scrollbar-track': {
                    borderRadius: '10px',
                    background: 'none',
                },
                '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: '#F5F5F5',
                },
                '&::-webkit-scrollbar-thumb': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                },
            },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        currentEmployer,
        tiers,
        isProfileDialogOpen,
        setIsProfileDialogOpen,
        sendMessageDialogOpen,
        setSendMessageDialogOpen,
        setInterviewDialogOpen,
        interviewDialogOpen,
        jobSeeker,
        confirmDeleteSavedJobSeekerDialogOpen,
        setConfirmDeleteSavedJobSeekerDialogOpen,
        handleDeleteJob,
        handleCloseDialog,
        openJobInactiveDialogOpen,
        setOpenJobInactiveDialogOpen,
    } = params;

    const { icons: iconsCls, boldFont: boldFontCls, flexContainer: flexContainerCls } = classes;
    const { listType, open, job, isSavedList } = props;

    const isListTypeSavedJs = listType === EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED;

    const jobSeekerId = _get(jobSeeker, 'userId', null);
    const savedDt = _get(jobSeeker, 'savedDt');
    const applyDt = _get(jobSeeker, 'applyDt');
    const jobSeekerName = getNameByUser(jobSeeker);
    const jobSeekerProfilePicUrl = _get(jobSeeker, 'profilePicUrl', null);

    const jobId = _get(job, 'uid', null);
    const jobTitle = _get(job, 'jobTitle', null);

    const jsEngagementRemainingCount = getEngagementRemainingCountByEmployerTiersData({ currentEmployer, tiers });
    const engagementRemainingCountText = getEngagementRemainingText(jsEngagementRemainingCount);

    const defns = {
        root: { open, maxWidth: 'lg', fullWidth: true, disableBackdropClick: true, disableEscapeKeyDown: true },
        closeButton: { onClick: handleCloseDialog },
        unreadNewApplicantBadge: {
            variant: 'dot',
            overlap: 'circle',
            color: 'primary',
        },
        engagementRemainingCountText: { variant: 'body1', color: 'secondary', value: engagementRemainingCountText },
        // avatar: { src: isProfilePicture ? profilePicUrl : '' },
        messageIcon: { color: 'secondary', className: iconsCls },
        calendarIcon: { color: 'primary', className: iconsCls },
        nameText: { variant: 'body1', className: boldFontCls },
        otherText: { variant: 'body2', className: boldFontCls },
        emailText: { variant: 'body2' },
        profileIcons: { className: iconsCls },
        messageContainer: { className: flexContainerCls },
        interviewContainer: { className: flexContainerCls },
        interviewDialog: {
            onClose: () => setInterviewDialogOpen(false),
            open: interviewDialogOpen,
            jobId,
            jobTitle,
            // jsEngageCount,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl /*limitExceeded, jobSeeker*/,
        },
        sendMessageDialog: {
            onClose: () => setSendMessageDialogOpen(false),
            open: sendMessageDialogOpen,
            jobId,
            jobTitle,
            // jsEngageCount,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl /* limitExceeded, recipient: jobSeeker*/,
        },
        profileDialog: {
            onClose: () => setIsProfileDialogOpen(false),
            open: isProfileDialogOpen,
            // jobId,
            // jobTitle,
            // jsEngageCount,
            jobSeekerId,
            jobSeekerApplySavedDt: isSavedList ? savedDt : applyDt,
            isSaved: isSavedList,
            isHideSavedToMyJobs: true,
            // jobSeekerName,
            // jobSeekerProfilePicUrl,
            // listType /*limitExceeded, jobSeeker*/,
        },
        tooltipInteriview: { title: 'Request Interview' },
        tooltipMessage: { title: 'Send Message' },
        tooltip: { title: 'View Profile' },

        confirmDeleteSavedJobSeekerDialog: {
            open: confirmDeleteSavedJobSeekerDialogOpen,
            onClose: () => setConfirmDeleteSavedJobSeekerDialogOpen(false),
            onConfirm: handleDeleteJob,
            message: isListTypeSavedJs ? CONFIRM_DIALOG_MESSAGE_DELETE_SAVED_JS : CONFIRM_DIALOG_MESSAGE_DELETE_SAVED_APPLICANT,
            title: isListTypeSavedJs ? CONFIRM_DIALOG_TITLE_DELETE_SAVED_JS : CONFIRM_DIALOG_TITLE_DELETE_SAVED_APPLICANT,
            color: 'default',
        },
        makeJobActiveDialog: {
            open: openJobInactiveDialogOpen,
            onClose: () => setOpenJobInactiveDialogOpen(false),
            onConfirm: () => setOpenJobInactiveDialogOpen(false),
            message: 'You cannot engage a Job Seeker when a job is Inactive',
            title: 'Job Listing is Inactive',
            yesButtonLabel: 'Okay',
            hideNoButton: true,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobSeekersDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    const { sys, employerId, currentEmployer } = appContext;
    const { handleDeleteSavedJobSeeker, handleDeleteApplicant, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const tiers = _get(sys, 'tiers');
    const tier = _get(currentEmployer, 'tier');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
    const [confirmDeleteSavedJobSeekerDialogOpen, setConfirmDeleteSavedJobSeekerDialogOpen] = useState(false);
    const [openJobInactiveDialogOpen, setOpenJobInactiveDialogOpen] = useState(false);
    const [jobSeeker, setJobSeeker] = useState();
    // const [localUnreadNewApplicants, setLocalUnreadNewApplicants] = useState(null);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { listType, jobSeekers, job, unreadNewApplicants = [], onClose } = props;
    const jobId = _get(job, 'uid');
    const jobTitle = _get(job, 'jobTitle');
    const isJobActive = _get(job, 'active');

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const isSaved = listType === EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_SAVED;
    const remainingEngagementVisible = tier !== 3;

    //---------------------------------------------------------------------
    // side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleDeleteJob = async () => {
        const isApplicant = listType === EMP_MY_JOBS_JOB_SEEKERS_LIST_TYPE_APPLIED;
        const jobId = _get(job, 'uid');
        const jobSeekerData = { userId: jobSeeker?.userId };

        if (isApplicant) {
            deleteApplicantsFromMyJobs({
                employerId,
                jobSeekerData,
                jobId,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                callback: () => {
                    setConfirmDeleteSavedJobSeekerDialogOpen(false);

                    handleDeleteApplicant && handleDeleteApplicant({ job, jobSeekerId: jobSeeker?.userId, jobId });
                },
            });
        } else {
            deleteSavedJobSeekerFromMyJobs({
                employerId,
                jobSeekerData,
                jobId,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                callback: () => {
                    setConfirmDeleteSavedJobSeekerDialogOpen(false);

                    handleDeleteSavedJobSeeker && handleDeleteSavedJobSeeker({ job, jobSeekerId: jobSeeker?.userId, jobId });
                },
            });
        }
    };

    const handleCloseDialog = () => {
        if (unreadNewApplicants.length > 0) {
            clearUnreadNewApplicants({ employerId, jobId });
        }
        onClose();
    };

    const handleClickRequestInterview = el => {
        if (isJobActive) {
            setJobSeeker(el);
            setInterviewDialogOpen(true);
        } else setOpenJobInactiveDialogOpen(true);
    };

    const handleClickSendMessage = el => {
        if (isJobActive) {
            setJobSeeker(el);
            setSendMessageDialogOpen(true);
        } else setOpenJobInactiveDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ remainingEngagementVisible })();
    const defns = getDefns({
        props,
        classes,
        currentEmployer,
        tiers,
        isProfileDialogOpen,
        setIsProfileDialogOpen,
        sendMessageDialogOpen,
        setSendMessageDialogOpen,
        setInterviewDialogOpen,
        interviewDialogOpen,
        isSaved,
        jobSeeker,
        confirmDeleteSavedJobSeekerDialogOpen,
        setConfirmDeleteSavedJobSeekerDialogOpen,
        handleDeleteJob,
        handleCloseDialog,
        openJobInactiveDialogOpen,
        setOpenJobInactiveDialogOpen,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <Dialog {...defns.root}>
                <DialogTitle {...defns.dialogTitle}>
                    <div {...defns.title}>
                        {`Job seekers ${isSaved ? 'saved' : 'applied'} to this job -`} <span {...defns.jobTitleText}>{jobTitle}</span>
                    </div>
                </DialogTitle>
                <DialogContent {...defns.listMainContainer}>
                    <div>
                        {remainingEngagementVisible && (
                            <div {...defns.engagementRemainingCountContainer}>
                                <AfitdTypography {...defns.engagementRemainingCountText} />
                            </div>
                        )}
                        {_map(jobSeekers, (el, idx) => {
                            const userId = _get(el, 'userId');
                            const accountDeleteDt = _get(el, 'accountDeleteDt');
                            const accountDeleted = !!accountDeleteDt;
                            const unreadNewApplicantBadgeInvisible = !_includes(unreadNewApplicants, userId);
                            const interviewIconButtonTooltipDefn = accountDeleted ? { title: 'Job seeker is no longer available' } : defns.tooltipInteriview;
                            const messageIconButtonTooltipDefn = accountDeleted ? { title: 'Job seeker is no longer available' } : defns.tooltipMessage;
                            const profileIconButtonTooltipDefn = accountDeleted ? { title: 'Job seeker is no longer available' } : defns.tooltip;
                            return (
                                <div key={getUniqueKey()}>
                                    {smMatches ? (
                                        <div {...defns.container}>
                                            <div {...defns.profilePicMainContainer}>
                                                <div {...defns.profilePicContainer}>
                                                    <Badge {...defns.unreadNewApplicantBadge} invisible={unreadNewApplicantBadgeInvisible}>
                                                        <Avatar {...defns.avatar} src={el?.profilePicUrl}>
                                                            ?
                                                        </Avatar>
                                                    </Badge>
                                                </div>
                                                <div {...defns.nameContainer}>
                                                    <AfitdTypography {...defns.nameText}>{`${el?.firstName} ${el?.lastName}`}</AfitdTypography>
                                                </div>
                                            </div>
                                            <div {...defns.dialogButtonContainerSm}>
                                                <Tooltip {...interviewIconButtonTooltipDefn}>
                                                    <div {...defns.requestInterviewContainer}>
                                                        <AfitdButton
                                                            disabled={accountDeleted}
                                                            classes={{ label: classes.buttonLabel }}
                                                            startIcon={<CalendarTodayIcon color="primary" />}
                                                            variant="link"
                                                            label="Request Interview"
                                                            onClick={() => handleClickRequestInterview(el)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                                <div {...defns.twoButtonsContainerSm}>
                                                    <Tooltip {...messageIconButtonTooltipDefn}>
                                                        <div {...defns.sendMessageContainer}>
                                                            <AfitdButton
                                                                disabled={accountDeleted}
                                                                classes={{ label: classes.buttonLabel }}
                                                                startIcon={<MessageIcon color="secondary" />}
                                                                variant="link"
                                                                label="Message"
                                                                onClick={() => handleClickSendMessage(el)}
                                                                title="hello"
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                    <div {...defns.viewProfileContainer}>
                                                        <Tooltip {...profileIconButtonTooltipDefn}>
                                                            {/* <AccountCircleIcon
                                                                {...defns.profileIcons}
                                                                onClick={() => {
                                                                    setJobSeeker(el);
                                                                    setIsProfileDialogOpen(true);
                                                                }}
                                                            /> */}
                                                            {!accountDeleted ? (
                                                                <AfitdButton
                                                                    classes={{ label: classes.buttonLabel }}
                                                                    startIcon={<AccountCircleIcon />}
                                                                    variant="link"
                                                                    label="Profile"
                                                                    onClick={() => {
                                                                        setJobSeeker(el);
                                                                        setIsProfileDialogOpen(true);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <AccountCircleIcon {...defns.iconsDisabled} />
                                                            )}
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                                <div {...defns.deleteButtonContainer}>
                                                    {/* <CloseIcon
                                                        {...defns.icons}
                                                        onClick={() => {
                                                            setJobSeeker(el);
                                                            setConfirmDeleteSavedJobSeekerDialogOpen(true);
                                                        }}
                                                    /> */}
                                                    <AfitdButton
                                                        classes={{ label: classes.buttonLabel }}
                                                        startIcon={<CloseIcon />}
                                                        variant="link"
                                                        label="Delete"
                                                        onClick={() => {
                                                            setJobSeeker(el);
                                                            setConfirmDeleteSavedJobSeekerDialogOpen(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div {...defns.container}>
                                            <div {...defns.profilePicMainContainer}>
                                                <div {...defns.profilePicContainer}>
                                                    <Badge {...defns.unreadNewApplicantBadge} invisible={unreadNewApplicantBadgeInvisible}>
                                                        <Avatar {...defns.avatar} src={el?.profilePicUrl}>
                                                            ?
                                                        </Avatar>
                                                    </Badge>
                                                </div>
                                                <div {...defns.nameContainer}>
                                                    <AfitdTypography {...defns.nameText}>{`${el?.firstName} ${el?.lastName}`}</AfitdTypography>
                                                </div>
                                            </div>

                                            <Tooltip {...interviewIconButtonTooltipDefn}>
                                                <div {...defns.requestInterviewContainer}>
                                                    <AfitdButton
                                                        disabled={accountDeleted}
                                                        classes={{ label: classes.buttonLabel }}
                                                        startIcon={<CalendarTodayIcon color="primary" />}
                                                        variant="link"
                                                        label="Request Interview"
                                                        onClick={() => handleClickRequestInterview(el)}
                                                    />
                                                </div>
                                            </Tooltip>
                                            <Tooltip {...messageIconButtonTooltipDefn}>
                                                <div {...defns.sendMessageContainer}>
                                                    <AfitdButton
                                                        disabled={accountDeleted}
                                                        classes={{ label: classes.buttonLabel }}
                                                        startIcon={<MessageIcon color="secondary" />}
                                                        variant="link"
                                                        label="Send Message"
                                                        onClick={() => handleClickSendMessage(el)}
                                                    />
                                                </div>
                                            </Tooltip>
                                            <div {...defns.viewProfileContainer}>
                                                <Tooltip {...profileIconButtonTooltipDefn}>
                                                    {!accountDeleted ? (
                                                        <AccountCircleIcon
                                                            {...defns.profileIcons}
                                                            onClick={() => {
                                                                setJobSeeker(el);
                                                                setIsProfileDialogOpen(true);
                                                            }}
                                                        />
                                                    ) : (
                                                        <AccountCircleIcon {...defns.iconsDisabled} />
                                                    )}
                                                </Tooltip>
                                            </div>
                                            <div {...defns.deleteButtonContainer}>
                                                <DeleteIcon
                                                    {...defns.icons}
                                                    onClick={() => {
                                                        setJobSeeker(el);
                                                        setConfirmDeleteSavedJobSeekerDialogOpen(true);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
                <DialogActions>
                    <AfitdButton {...defns.closeButton}>Close</AfitdButton>
                </DialogActions>
            </Dialog>
            {isProfileDialogOpen && <ProfileDialog {...defns.profileDialog} />}
            {interviewDialogOpen && <InterviewDialog {...defns.interviewDialog} />}
            {sendMessageDialogOpen && <SendMessageDialog {...defns.sendMessageDialog} />}
            <AfitdConfirmDialog {...defns.confirmDeleteSavedJobSeekerDialog} />
            <AfitdConfirmDialog {...defns.makeJobActiveDialog} />
        </>
    );
};

export default JobSeekersDialog;
